import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to sync state with URL query parameters
 * Enables filter persistence when navigating back/forward or refreshing
 *
 * @param {string} key - URL parameter key (e.g., 'q', 'page', 'wuchsformen')
 * @param {*} defaultValue - Default value when parameter is not in URL
 * @param {Object} options - Configuration options
 * @param {Function} options.serialize - Convert state to URL string (default: toString)
 * @param {Function} options.deserialize - Convert URL string to state (default: identity)
 * @returns {Array} [value, setValue] - Same API as useState
 */
export function useUrlState(key, defaultValue, options = {}) {
    const {
        serialize = (val) => String(val),
        deserialize = (val) => val,
    } = options;

    // Get initial value from URL or use default
    const getInitialValue = () => {
        if (typeof window === 'undefined') return defaultValue;

        const params = new URLSearchParams(window.location.search);
        const urlValue = params.get(key);

        if (urlValue === null) return defaultValue;

        try {
            return deserialize(urlValue);
        } catch (e) {
            console.warn(`Failed to deserialize URL param "${key}":`, e);
            return defaultValue;
        }
    };

    const [value, setValue] = useState(getInitialValue);
    const isInitialMount = useRef(true);

    // Update URL when value changes (skip on initial mount)
    useEffect(() => {
        // Skip URL update on initial mount to avoid unnecessary history entries
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const params = new URLSearchParams(window.location.search);

        // Determine if value is "empty" (should be removed from URL)
        const isEmpty =
            value === defaultValue ||
            value === '' ||
            value === null ||
            value === undefined ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'object' && Object.keys(value).length === 0);

        if (isEmpty) {
            params.delete(key);
        } else {
            params.set(key, serialize(value));
        }

        // Update URL without page reload
        const newUrl = params.toString()
            ? `${window.location.pathname}?${params.toString()}`
            : window.location.pathname;

        window.history.pushState({}, '', newUrl);
    }, [value, key, serialize, defaultValue]);

    // Listen for browser back/forward button
    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const urlValue = params.get(key);

            if (urlValue === null) {
                setValue(defaultValue);
            } else {
                try {
                    setValue(deserialize(urlValue));
                } catch (e) {
                    console.warn(`Failed to deserialize URL param "${key}":`, e);
                    setValue(defaultValue);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [key, defaultValue, deserialize]);

    return [value, setValue];
}

/**
 * Hook for managing object state in URL (e.g., filter groups)
 * Serializes object as multiple query parameters
 *
 * Example: { wuchsformen: ['baum'], laub: ['sommer'] }
 * → ?wuchsformen=baum&laub=sommer
 */
export function useUrlObjectState(defaultValue = {}) {
    const getInitialValue = () => {
        if (typeof window === 'undefined') return defaultValue;

        const params = new URLSearchParams(window.location.search);
        const result = {};

        // Parse all filter-related params
        for (const [key, value] of params.entries()) {
            // Skip non-filter params (q, page, category)
            if (key === 'q' || key === 'page' || key === 'category') continue;

            // Split comma-separated values into array
            result[key] = value.split(',').filter(Boolean);
        }

        return Object.keys(result).length > 0 ? result : defaultValue;
    };

    const [value, setValue] = useState(getInitialValue);
    const isInitialMount = useRef(true);

    // Update URL when value changes
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const params = new URLSearchParams(window.location.search);

        // Preserve non-filter params
        const q = params.get('q');
        const page = params.get('page');
        const category = params.get('category');

        // Clear all params
        const newParams = new URLSearchParams();

        // Restore non-filter params
        if (q) newParams.set('q', q);
        if (page) newParams.set('page', page);
        if (category) newParams.set('category', category);

        // Add filter params
        Object.entries(value).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0) {
                newParams.set(key, values.join(','));
            }
        });

        // Update URL
        const newUrl = newParams.toString()
            ? `${window.location.pathname}?${newParams.toString()}`
            : window.location.pathname;

        window.history.pushState({}, '', newUrl);
    }, [value]);

    // Listen for browser back/forward
    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const result = {};

            for (const [key, val] of params.entries()) {
                if (key === 'q' || key === 'page' || key === 'category') continue;
                result[key] = val.split(',').filter(Boolean);
            }

            setValue(Object.keys(result).length > 0 ? result : defaultValue);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [defaultValue]);

    return [value, setValue];
}
